import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from 'lib/utils';
import { PaymentMethodMark } from 'components/payments/PaymentMethodMark';
import type { PaymentMethod } from 'modules/payments/methods';

interface PaymentMethodPickerProps {
  methods: PaymentMethod[];
  value: PaymentMethod | null;
  onChange: (method: PaymentMethod) => void;
  isLoading: boolean;
}

/** One button per offered method (Payme, Click, Uzum, Uzcard, Humo, card transfer), each with its own logo. */
export function PaymentMethodPicker({ methods, value, onChange, isLoading }: PaymentMethodPickerProps) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-foreground">{t('payment_method', 'Payment method')}</p>
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
      ) : methods.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('no_payment_methods', 'Online payment is not available right now. Please try again later.')}
        </p>
      ) : (
        <div className={cn('grid gap-2', methods.length === 1 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3')}>
          {methods.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => onChange(id)}
              aria-pressed={value === id}
              className={cn(
                'flex items-center justify-center rounded-md border bg-card px-2 py-3 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                value === id
                  ? 'border-foreground text-foreground ring-1 ring-foreground'
                  : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
              )}
            >
              <PaymentMethodMark method={id} cardLabel={t('payment_card_transfer', 'Card transfer')} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
